import { useEffect, useRef, useState } from 'react';
import { asset } from './asset';
import { useIPortfolio } from './useIPortfolio';
import CardSwap, { Card } from './CardSwap';
import ShapeBlur from './ShapeBlur';
import HeroReactorVideo from './HeroReactorVideo';
import ChatWidget from './chat/ChatWidget';
import './ShapeBlur.css';
import './PhotoAlbum.css';
import { PHOTO_ALBUM_IMAGES } from './photoAlbumData';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'aos/dist/aos.css';
import 'glightbox/dist/css/glightbox.min.css';
import 'swiper/css';
import 'swiper/css/pagination';
import './main.css';

const CONTACT_EMAIL = 'ehsan.faghih72@gmail.com';
const ALBUM_PAGE_URL = `${window.location.origin}/album`;

const PERSONAL_INTERESTS = [
  { img: 'img/AboutMe-album-poetry.png', title: 'Poetry', desc: 'Writing and reading poetry in Persian.', gallery: 'personal-interests-gallery' },
  { img: 'img/AboutMe-album-movie.png', title: 'Movies & Series', desc: 'Thought-provoking films and dramatic storytelling.', gallery: 'personal-interests-gallery' },
  { img: 'img/AboutMe-album-cooking.png', title: 'Cooking', desc: 'Trying new recipes and sharing meals with friends.', gallery: 'personal-interests-gallery' },
  { img: 'img/AboutMe-album-basket.png', title: 'Basketball', desc: 'A lifelong love of the game since childhood.', gallery: 'personal-interests-gallery' },
  { img: 'img/AboutMe-album-mnu.png', title: 'Football', desc: 'Following Manchester United and Esteghlal FC.', gallery: 'personal-interests-gallery' },
  { img: 'img/AboutMe-album-camping.png', title: 'Camping', desc: 'Campfires, barbecues, and time in nature.', gallery: 'personal-interests-gallery' },
  { img: 'img/AboutMe-album-friend.png', title: 'Friendship', desc: 'Meaningful connections and shared memories.', gallery: 'personal-interests-gallery' },
  { img: 'img/AboutMe-album-shopping.png', title: 'Shopping', desc: 'Browsing malls and finding new styles.', gallery: 'personal-interests-gallery' },
  { img: 'img/AboutMe-album-pet.png', title: 'Pets', desc: 'Enjoying time with friends\' pets.', gallery: 'personal-interests-gallery' },
  { img: 'img/AboutMe-album-hystory.png', title: 'History', desc: 'Exploring historical events and their lessons.', gallery: 'personal-interests-gallery' },
  { img: 'img/AboutMe-album-watch.png', title: 'DIY & Learning', desc: 'Trying new skills—from baking to fixing things myself.', gallery: 'personal-interests-gallery' },
];

const POETRY_BOOK_POEM_FA = `آه از آن دم که دلت برگ خزانی باشد
خُرد در زیر قدمهای گرانی باشد
 از سرِ تلخیِ او یا که خوشی ها، بینِ
عشق یا حسِّ تنفر نوسانی باشد`;

const POETRY_BOOK_POEM_EN = `Alas for the moment when your heart is like an autumn leaf,
Broken beneath the weight of heavy steps.
Alas for the moment when her bitterness,
Or the sweetness of memories long past,
Leaves your heart forever wavering
Between love and hate.`;

const ADVISOR_DEGREES = [
  {
    title: 'Ph.D. Degree',
    focusTitle: 'Research focus',
    focus:
      'Applying machine learning and computer vision to practical textile and recycling challenges, including automated textile sorting, material classification, model benchmarking, and accuracy evaluation. Current work focuses on developing models for classifying blended textile materials by combining 2D image-based features with 1D spectral or numerical data, with the goal of supporting reliable, scalable industrial decision-making and waste reduction.',
    advisors: [
      {
        role: 'Advisor',
        name: 'Dr. Zahra Saki',
        email: 'zsaki@ncsu.edu',
        scholar: 'https://scholar.google.com/citations?hl=en&user=D7vz1JMAAAAJ',
        img: 'img/advisor.jpg',
        delay: 100,
      },
      {
        role: 'Co-advisor',
        name: 'Dr. Marguerite Moore',
        email: 'marguerite_moore@ncsu.edu',
        scholar: 'https://scholar.google.com/citations?user=VwvqDJcAAAAJ&hl=en',
        img: 'img/advisor2.jpg',
        delay: 200,
      },
    ],
  },
  {
    title: "Master's Degree",
    focusTitle: 'Research focus',
    focus:
      'Quantum circuit design with an emphasis on multiplication; exploring circuit architectures and arithmetic techniques to improve the efficiency of quantum multiplication approaches.',
    advisors: [
      {
        role: 'Advisor',
        name: 'Dr. Keivan Navi',
        email: 'knavi@cpp.edu',
        scholar: 'https://scholar.google.com/citations?hl=en&user=QEs-nLIAAAAJ',
        img: 'img/advisor3.jpg',
        delay: 100,
      },
      {
        role: 'Ph.D. supervisor',
        name: 'Dr. Mohammadreza Taheri',
        scholar: 'https://scholar.google.com/citations?user=zzm6rGwAAAAJ&hl=en',
        img: 'img/advisor4.jpg',
        delay: 200,
      },
    ],
  },
];

const TESTIMONIALS = [
  {
    img: 'img/advisor4.jpg',
    name: 'Dr. Mohammadreza Taheri',
    role: 'Ph.D. supervisor',
    quote:
      'Working with Ehsan in quantum computing research was a true privilege. He combines qualities that rarely come together in one person: exceptional intelligence, genuine creativity, technical depth, and the persistence to keep going when problems become truly difficult. What impressed me most was his ability to push ideas further than expected and find original paths through challenges that would stop many others. Anyone who can stand out in a field as demanding as quantum computing has the potential to excel far beyond it; and Ehsan is exactly that kind of person.',
  },
];

const SKILL_CATEGORIES = [
  {
    title: 'AI Orchestrator',
    description: "I get projects done by managing AI tools, not just prompting them. Vibe coding is useful if you also check the code, debug it, run tests, and look for security issues. I have models criticize each other's results. These days, everything is about orchestration.",
  },
  { title: 'Programming', skills: ['Python', 'SQL'] },
  { title: 'Machine Learning', skills: ['scikit-learn', 'PyTorch', 'CNN and ViT models', 'Prototyping ML models', 'Data preprocessing'] },
  { title: 'Data & Visualization', skills: ['NumPy', 'Pandas', 'Matplotlib', 'Seaborn'] },
  { title: 'Tooling', skills: ['Git', 'Jupyter', 'LaTeX', 'CursorAI'] },
  { title: 'Soft skills', skills: ['Leadership', 'Collaboration', 'Technical communication', 'Analytical reasoning', 'Self-starting', 'Taking initiative'] },
];

const SKILLS_LEFT = SKILL_CATEGORIES.slice(0, 3);
const SKILLS_RIGHT = SKILL_CATEGORIES.slice(3);

const PROJECT_SAMPLES = [
  {
    title: 'Traveler',
    subtitle: 'Constraint-based trip discovery',
    description:
      'A full-stack TypeScript web app that finds trips from a user’s real schedule, budget, and preferences, instead of starting with a destination. It connects Google Calendar with OAuth, figures out when they are actually free, searches flights and lodging through provider adapters, and ranks options with scores you can explain. The work covers API-first architecture, domain logic kept out of the UI, secure token handling, and a responsive product interface. That mix of product thinking, integrations, and security is what hiring managers in tech tend to notice.',
  },
  {
    title: 'Cyrus',
    subtitle: 'Voice-powered personal AI assistant',
    description:
      'I built Cyrus as a voice-first assistant that can actually do things, not just chat. You talk to it, it talks back, and an agent loop on a Node.js backend decides when to pull from Gmail, Calendar, Telegram, or a live browser. The frontend is a custom dashboard, with OAuth, streaming speech, and an approval step before anything irreversible happens. It is the kind of project that shows full-stack work, API design, and hands-on experience shipping LLM agents that talk to real tools.',
  },
  {
    title: 'Machine Learning Optimization Research Co-op',
    description: 'Optimized ML classification models for prostate cancer detection, improving accuracy by 8% through hyperparameter tuning and performance analysis on biomedical data.',
  },
  {
    title: 'Optimization & Gradient Descent Visualizer',
    description: 'Built a from-scratch Python visualizer to study model behavior during gradient descent, with interactive optimization curve analysis for learning and experimentation.',
  },
  {
    title: 'Textile Fiber Classification with CNNs and Vision Transformers',
    description: 'Benchmarked CNN and Vision Transformer architectures for textile sorting, achieving an 11% improvement in fiber recognition accuracy over baseline models.',
  },
  {
    title: 'SMS Spam Classification Pipeline',
    description: 'Developed an end-to-end system comparing TF-IDF + Naive Bayes (98% accuracy) with fine-tuned DistilBERT (99% accuracy), reaching a 96.6% F1-score.',
  },
  {
    title: 'RAG-Based Question Answering',
    description: 'Implemented a retrieval-augmented QA pipeline with text chunking, embedding generation, vector indexing, and LLM-based answer synthesis.',
  },
  {
    title: 'AI-Driven Email Automation',
    description: 'Designed autonomous AI agents that read, classify, and draft responses to incoming emails—reducing manual triage while keeping replies context-aware and on-brand.',
  },
  {
    title: 'Financial Issue Classifier & Resolver',
    description: 'Built AI agents that extract finance-related issues from user reviews, classify them in real time, and surface instant responses with actionable solution recommendations.',
  },
];

const PROJECTS_LEFT = PROJECT_SAMPLES.filter((_, index) => index % 2 === 0);
const PROJECTS_RIGHT = PROJECT_SAMPLES.filter((_, index) => index % 2 === 1);

const ABOUT_INTRO = [
  'Calmness and kindness are the qualities most often reflected back to me by my closest friends. I value the human side of life deeply, even in academic and professional spaces, and I try to build warm, sincere connections with the people around me.',
  'Professionally, my work sits at the intersection of machine learning, computer vision, and practical industrial problem-solving. With a background in computer engineering and Ph.D.-level research in AI applications, I focus on developing data-driven models for classification, decision support, and real-world use. My current research explores how AI and computer vision can improve textile sorting and recycling through material recognition, model benchmarking, and scalable industrial workflows.',
  'Outside my technical work, I deeply cherish composing poetry in Persian, my mother tongue. It brings me clarity, peace, and distance from negative thoughts.',
  'If I were to introduce myself through an image, I would imagine a vast green meadow under a calm blue sky, people smiling and walking freely, and myself sitting quietly beneath a walnut tree, immersed in the beauty around me and untouched by the noise of the modern world.',
];

function SkillCategory({ title, skills, description }) {
  return (
    <div className="skill-category">
      <h4>{title}</h4>
      <p>{description || skills.join(', ')}</p>
    </div>
  );
}

function ProjectSample({ title, subtitle, description }) {
  return (
    <div className="skill-category">
      <h4>{title}</h4>
      {subtitle && <p className="project-subtitle">{subtitle}</p>}
      <p>{description}</p>
    </div>
  );
}

function PoetryBookSection({ expanded, onToggle }) {
  return (
    <div className="poetry-book-subsection" data-aos="fade-up" data-aos-delay="150">
      <h3 className="poetry-book-title">A Palace of Loneliness, My Persian Poetry Book</h3>
      <p className="poetry-book-tagline">Registered and published book in my native language, Persian.</p>

      <div className="poetry-book-card">
        <div className="row gy-4 align-items-center">
          <div className="col-md-4 col-lg-3 text-center">
            <img
              src={asset('img/myBook.jpg')}
              className="poetry-book-cover img-fluid"
              alt="Kakh-e Tanhaei book cover"
            />
          </div>
          <div className="col-md-8 col-lg-9">
            <h4 className="poetry-book-name">Kakh-e Tanhaei</h4>
            <p className="poetry-book-subtitle">The Palace of Loneliness</p>
            <p className="poetry-book-teaser">
            A collection of romantic and realistic poems rooted in lived experience and emotional reflection. The title speaks to a familiar kind of loneliness: the feeling of being far from the people, places, or memories one holds dear.
            </p>
            <button
              type="button"
              className={`poetry-book-toggle${expanded ? ' expanded' : ''}`}
              onClick={onToggle}
              aria-expanded={expanded}
              aria-controls="poetry-book-details"
            >
              <span>More info</span>
              <i className="bi bi-chevron-down" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div
          id="poetry-book-details"
          className={`poetry-book-details${expanded ? ' show' : ''}`}
          aria-hidden={!expanded}
        >
          <p>
            I have always loved poetry and writing poems. I began writing poetry when I was around 18,
            and I was able to publish and register my poems as a book in my native language, Persian.
            The title of the book is <strong>Kakh-e Tanhaei</strong>, which can be translated as{' '}
            <em>The Palace of Loneliness</em>.
          </p>
          <p>
            It is a kind of loneliness shaped by being far from the people you love, a feeling that is
            deeply familiar to those who have experienced immigration. Most of my poems are romantic,
            with a dark emotional tone. They reflect the deep feelings I had in certain moments, and in
            many ways, they still do.
          </p>
          <p>
            Maybe one day I will translate them into English, but for now, this is one of the poems I
            like, with a close English translation created with the help of machine translation:
          </p>

          <blockquote className="poetry-book-poem" dir="rtl" lang="fa">
            <i className="bi bi-quote poetry-book-quote-open" aria-hidden="true" />
            <p>{POETRY_BOOK_POEM_FA}</p>
            <i className="bi bi-quote poetry-book-quote-close" aria-hidden="true" />
          </blockquote>

          <blockquote className="poetry-book-translation">
            <p>{POETRY_BOOK_POEM_EN}</p>
          </blockquote>

          <p className="poetry-book-note">
            By the way, Persian literature is full of beautiful meanings and poems. Make sure you read
            the works of Rumi, Omar Khayyam, and Hafez at least once.
          </p>
          <p className="poetry-book-note">
            If you know a wonderful book, poet, or writer from another country or language that you
            truly love, I would love to hear your suggestion. I would really appreciate it!
          </p>
        </div>
      </div>
    </div>
  );
}

function IPortfolioPage() {
  useIPortfolio();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [messageStatus, setMessageStatus] = useState('idle');
  const [emailCopied, setEmailCopied] = useState(false);
  const [copyToastKey, setCopyToastKey] = useState(0);
  const [poetryBookExpanded, setPoetryBookExpanded] = useState(false);
  const scrollAreaRef = useRef(null);
  const copyTimeoutRef = useRef(null);

  const showEmailCopiedToast = () => {
    setCopyToastKey((key) => key + 1);
    setEmailCopied(true);
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = setTimeout(() => setEmailCopied(false), 2500);
  };

  const handleCopyEmail = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const copyWithFallback = () => {
      const textarea = document.createElement('textarea');
      textarea.value = CONTACT_EMAIL;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '0';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, CONTACT_EMAIL.length);

      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch {
        copied = false;
      }

      document.body.removeChild(textarea);
      return copied;
    };

    showEmailCopiedToast();

    if (copyWithFallback()) {
      return;
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(CONTACT_EMAIL).catch(() => {});
    }
  };

  const handleDirectMessageSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    setMessageStatus('loading');

    try {
      const response = await fetch('https://formsubmit.co/ajax/ehsan.faghih72@gmail.com', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.get('name'),
          email: formData.get('email'),
          message: formData.get('message'),
          _subject: 'New direct message from your portfolio',
          _template: 'table',
          _captcha: 'false',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      form.reset();
      setMessageStatus('success');
    } catch {
      setMessageStatus('error');
    }
  };

  useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return undefined;

    const updateHint = () => {
      const hasOverflow = el.scrollHeight > el.clientHeight + 2;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
      setShowScrollHint(hasOverflow && !atBottom);
    };

    updateHint();
    el.addEventListener('scroll', updateHint);
    window.addEventListener('resize', updateHint);
    const observer = new ResizeObserver(updateHint);
    observer.observe(el);

    return () => {
      el.removeEventListener('scroll', updateHint);
      window.removeEventListener('resize', updateHint);
      observer.disconnect();
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const scrollSidebarDown = () => {
    scrollAreaRef.current?.scrollBy({ top: 140, behavior: 'smooth' });
  };

  return (
    <>
      <header
        id="header"
        className={`header dark-background d-flex flex-column${sidebarCollapsed ? ' header-collapsed' : ''}`}
      >
        <i className="header-toggle d-xl-none bi bi-list" />

        <button
          type="button"
          className="header-collapse-btn d-none d-xl-flex"
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={() => setSidebarCollapsed((prev) => !prev)}
        >
          <i className={`bi ${sidebarCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`} />
        </button>

        <div className="header-scroll-area" ref={scrollAreaRef}>
          <div className="profile-img">
            <div className="profile-img-frame">
              <img src={asset('img/my-profile-img.jpg')} alt="Ehsan Faghih" className="img-fluid rounded-circle" />
              <ShapeBlur
                className="profile-shape-blur"
                variation={2}
                shapeSize={1}
                roundness={0.5}
                borderSize={0.05}
                circleSize={0.25}
                circleEdge={1}
              />
            </div>
          </div>

          <a href="#hero" className="logo d-flex align-items-center justify-content-center">
            <h1 className="sitename">Ehsan Faghih</h1>
          </a>

          <div className="social-links-wrap">
            <div className="social-links text-center">
              <button
                type="button"
                className="gmail"
                onClick={handleCopyEmail}
                aria-label="Gmail"
                data-tooltip="Gmail"
              >
                <i className="bi bi-envelope-at-fill" />
              </button>
              <a
                href="https://scholar.google.com/citations?user=1xQoOFYAAAAJ&hl=en&oi=ao"
                className="scholar"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Google Scholar"
                data-tooltip="Google Scholar"
              >
                <i className="bi bi-mortarboard-fill" />
              </a>
              <a
                href="https://github.com/efaghih"
                className="github"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
                data-tooltip="GitHub"
              >
                <i className="bi bi-github" />
              </a>
              <a
                href="https://www.linkedin.com/in/ehsan-faghih-510650b3/"
                className="linkedin"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                data-tooltip="LinkedIn"
              >
                <i className="bi bi-linkedin" />
              </a>
            </div>
            {emailCopied && (
              <div
                className="email-copy-toast"
                role="status"
                aria-live="polite"
                key={copyToastKey}
              >
                Email copied: {CONTACT_EMAIL}
              </div>
            )}
          </div>

          <nav id="navmenu" className="navmenu">
            <ul>
              <li><a href="#hero" className="active"><i className="bi bi-house navicon" />Home</a></li>
              <li><a href="#about"><i className="bi bi-person navicon" /> About</a></li>
              <li><a href="#resume"><i className="bi bi-file-earmark-text navicon" /> Resume</a></li>
              <li><a href="#project-samples"><i className="bi bi-kanban navicon" /> Project Samples</a></li>
              <li><a href="#advisors"><i className="bi bi-mortarboard navicon" /> Advisors</a></li>
              <li><a href="#personal-interests"><i className="bi bi-heart navicon" /> Personal Interests</a></li>
              <li><a href="#contact"><i className="bi bi-chat-dots navicon" /> Direct Message</a></li>
            </ul>
          </nav>

          <ChatWidget />
        </div>

        {showScrollHint && (
          <button
            type="button"
            className="sidebar-scroll-hint"
            aria-label="Scroll sidebar down"
            onClick={scrollSidebarDown}
          >
            <i className="bi bi-chevron-down" />
          </button>
        )}
      </header>

      {sidebarCollapsed && (
        <button
          type="button"
          className="header-expand-tab d-none d-xl-flex"
          aria-label="Expand sidebar"
          onClick={() => setSidebarCollapsed(false)}
        >
          <i className="bi bi-chevron-right" />
        </button>
      )}

      <main className={`main${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
        <section id="hero" className="hero section dark-background">
          <HeroReactorVideo />
          <div className="container hero-content" data-aos="fade-up" data-aos-delay="100">
            <h2>Ehsan Faghih</h2>
            <p>
              I&apos;m{' '}
              <span
                className="typed"
                data-typed-items="AI Orchestrator, Computer Vision Explorer,Researcher and Problem Solver,Engaged in AI and ML Advancements,Idea Catalyst,Passionate About Where Sciences Meet,Thirsty to Learn New Things,ML and CV Enthusiast"
              >
                Computer Vision Explorer
              </span>
              <span className="typed-cursor typed-cursor--blink" aria-hidden="true" />
              <span className="typed-cursor typed-cursor--blink" aria-hidden="true" />
            </p>
            <p className="hero-work-auth">
              <i className="bi bi-patch-check-fill" aria-hidden="true" />
              Authorized to work in the United States (EAD)
            </p>
          </div>
        </section>

        <section id="about" className="about section">
          <div className="container section-title" data-aos="fade-up">
            <h2>About</h2>
            {ABOUT_INTRO.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          <div className="container" data-aos="fade-up" data-aos-delay="100">
            <div className="row gy-4 justify-content-center">
              <div className="col-lg-4">
                <img src={asset('img/bio-img.JPG')} className="img-fluid" alt="Ehsan Faghih" />
              </div>
              <div className="col-lg-8 content">
                <h2>Ph.D. Student &amp; Researcher</h2>
                <div className="row">
                  <div className="col-lg-6">
                    <ul>
                      <li><i className="bi bi-chevron-right" /> <strong>Birthday:</strong> <span>2 Nov 1993</span></li>
                      <li><i className="bi bi-chevron-right" /> <strong>NCSU Email:</strong> <span>sfaghih@ncsu.edu</span></li>
                      <li><i className="bi bi-chevron-right" /> <strong>Main Area:</strong> <span>AI & Quantum</span></li>
                    </ul>
                  </div>
                  <div className="col-lg-6">
                    <ul>
                      <li><i className="bi bi-chevron-right" /> <strong>City of Birth:</strong> <span>Qaen, Iran</span></li>
                      <li><i className="bi bi-chevron-right" /> <strong>City of residence:</strong> <span>Raleigh, USA</span></li>
                      <li><i className="bi bi-chevron-right" /> <strong>Work Availability:</strong> <span>Seeking Opportunities</span></li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="stats" className="stats section light-background">
          <div className="container" data-aos="fade-up" data-aos-delay="100">
            <div className="row gy-4">
              <div className="col-lg-3 col-md-6">
                <div className="stats-item">
                  <i className="bi bi-emoji-smile" />
                  <span data-purecounter-start="0" data-purecounter-end="5" data-purecounter-duration="1" className="purecounter" />
                  <p><strong>Academic Publications</strong> <span>Three journals, one conferences, and one book chapter</span></p>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="stats-item">
                  <i className="bi bi-journal-richtext" />
                  <span data-purecounter-start="0" data-purecounter-end="21" data-purecounter-duration="1" className="purecounter" />
                  <p><strong>Projects</strong> <span> AI/ML and Quantum area </span></p>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="stats-item">
                  <i className="bi bi-headset" />
                  <span data-purecounter-start="0" data-purecounter-end="54" data-purecounter-duration="1" className="purecounter" />
                  <p><strong>Citations</strong> <span></span></p>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="stats-item">
                  <i className="bi bi-people" />
                  <span data-purecounter-start="0" data-purecounter-end="4" data-purecounter-duration="1" className="purecounter" />
                  <p><strong>Ongoing Projects</strong> <span></span></p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="resume" className="resume section">
          <div className="container section-title" data-aos="fade-up">
            <h2>Resume</h2>
            <p>Magnam dolores commodi suscipit. Necessitatibus eius consequatur ex aliquid fuga eum quidem. Sit sint consectetur velit. Quisquam quos quisquam cupiditate. Et nemo qui impedit suscipit alias ea. Quia fugiat sit in iste officiis commodi quidem hic quas.</p>
          </div>

          <div className="container">
            <div className="row">
              <div className="col-lg-6" data-aos="fade-up" data-aos-delay="100">
                <h3 className="resume-title">Sumary</h3>
                <div className="resume-item pb-0">
                  <h4>Ehsan Faghih</h4>
                  <p><em>Machine Learning and Computer Vision specialist with experience developing data-driven solutions across diverse real-world datasets and industrial problem settings. Holds B.S. and M.S. degrees in Computer Engineering and is currently completing Ph.D.-level research focused on applying AI to practical classification and decision-support problems. Experienced in building, fine-tuning, and benchmarking deep learning models, including CNNs and Vision Transformers, with strong emphasis on data understanding, reproducible experimentation, performance evaluation, and translating research into practical prototypes for real-world use.</em></p>
                  <ul>
                    <li>Raleigh, NC</li>
                    <li>sfaghih@ncsu.edu</li>
                  </ul>
                </div>
                <h3 className="resume-title">Education</h3>
                <div className="resume-item">
                  <h4>Ph.D., Textile Technology Management</h4>
                  <h5>2024 - Present</h5>
                  <p><em>North Carolina State University, Raleigh, NC</em></p>
                </div>
                <div className="resume-item">
                  <h4>Ph.D. Program, Computer Engineering</h4>
                  <h5>2022 - 2024</h5>
                  <p><em>North Carolina State University, Raleigh, NC</em></p>
                  <p>Changed major to Textile Technology Management.</p>
                </div>
                <div className="resume-item">
                  <h4>Master of Science, Computer Engineering</h4>
                  <h5>2018 - 2021</h5>
                  <p><em>Shahid Beheshti University, Tehran, Iran</em></p>
                </div>
                <div className="resume-item">
                  <h4>Bachelor of Science, Computer Engineering</h4>
                  <h5>2012 - 2017</h5>
                  <p><em>Isfahan University of Technology, Isfahan, Iran</em></p>
                </div>
              </div>

              <div className="col-lg-6" data-aos="fade-up" data-aos-delay="200">
                <h3 className="resume-title">Publications</h3>
                <div className="resume-item">
                  <ul>
                    <li>
                      E. Faghih, Z. Saki, &ldquo;Synchronized Pitfalls Across Remote Sensing Networks in Non-Contiguous Validation,&rdquo; under review.
                    </li>
                    <li>
                      E. Faghih, M. Moore, Z. Saki, &ldquo;Benchmarking AI Architectures for Circularity in Textiles: Fiber Recognition with CNNs, ViTs, and Hybrid Models,&rdquo; 2025.{' '}
                      <a href="https://www.sciencedirect.com/science/article/pii/S0956053X26002989" target="_blank" rel="noopener noreferrer">Link</a>
                    </li>
                    <li>
                      E. Faghih, Z. Saki, M. Moore, &ldquo;A Systematic Literature Review — AI-Enabled Textile Waste Sorting,&rdquo; <em>Sustainability</em>, vol. 17, no. 10, Article 4264, 2025.{' '}
                      <a href="https://www.mdpi.com/2071-1050/17/10/4264" target="_blank" rel="noopener noreferrer">Link</a>
                    </li>
                    <li>
                      E. Faghih, Dr. Huiyang Zhou, &ldquo;Dynamic Runtime Assertions in Quantum Ternary Systems,&rdquo; IEEE International Conference on Quantum Computing and Engineering, QCE2024.{' '}
                      <a href="https://ieeexplore.ieee.org/abstract/document/10821299" target="_blank" rel="noopener noreferrer">Link</a>
                    </li>
                    <li>
                      E. Faghih, MR. Taheri, K. Navi, N. Bagherzadeh, &ldquo;Efficient Realization of Quantum Balanced Ternary Reversible Multiplier Building Blocks: A Great Step Towards Sustainable Computing,&rdquo; 2023.{' '}
                      <a href="https://www.sciencedirect.com/science/article/abs/pii/S221053792300063X" target="_blank" rel="noopener noreferrer">Link</a>
                    </li>
                  </ul>
                </div>
                <h3 className="resume-title">Conferences &amp; Talks</h3>
                <div className="resume-item">
                  <ul>
                    <li>
                      <strong>Computer-based Approaches in Textile Sorting</strong>, Textiles Research Symposium, Raleigh, NC, Spring 2025.
                    </li>
                    <li>
                      <strong>Benchmarking DL Model Performances in Textile Sorting</strong>, RISE Conference, Raleigh, NC, Fall 2025.
                    </li>
                    <li>
                      <strong>AI-Enabled Waste Sorting: Fine-Tuning ViT-Based Models for Textile Fiber Recognition</strong>, The Fiber Society Fall Conference, University of Delaware, Newark, DE, Oct. 22–24, 2025.
                    </li>
                    <li>
                      <strong>Dynamic Runtime Assertions in Quantum Ternary Systems</strong>, IEEE International Conference on Quantum Computing and Engineering, Montreal, QC, Sept. 15–20, 2024.
                    </li>
                  </ul>
                </div>
                <h3 className="resume-title">Book Chapter</h3>
                <div className="resume-item">
                  <ul>
                    <li>
                      F. Ashrafi, M. Moore, E. Faghih, &ldquo;Techno-economic analysis and market prospects for aquatic biorefinery products,&rdquo; Chapter 12.{' '}
                      <a href="https://www.sciencedirect.com/science/chapter/edited-volume/abs/pii/B9780443330346000030" target="_blank" rel="noopener noreferrer">Link</a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="project-samples" className="project-samples section light-background">
          <div className="container section-title" data-aos="fade-up">
            <h2>Project Samples</h2>
            <p>Selected research and engineering projects spanning machine learning, NLP, computer vision, and AI agent systems.</p>
          </div>

          <div className="container" data-aos="fade-up" data-aos-delay="100">
            <div className="row skills-content">
              <div className="col-lg-6">
                {PROJECTS_LEFT.map((project) => (
                  <ProjectSample key={project.title} {...project} />
                ))}
              </div>
              <div className="col-lg-6">
                {PROJECTS_RIGHT.map((project) => (
                  <ProjectSample key={project.title} {...project} />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="skills" className="skills section">
          <div className="container section-title" data-aos="fade-up">
            <h2>Skills</h2>
            <p>My skills are a blend of technical expertise and soft skills, reflecting my approach to problem-solving and collaboration.</p>
          </div>

          <div className="container" data-aos="fade-up" data-aos-delay="100">
            <div className="row skills-content">
              <div className="col-lg-6">
                {SKILLS_LEFT.map((category) => (
                  <SkillCategory key={category.title} {...category} />
                ))}
              </div>
              <div className="col-lg-6">
                {SKILLS_RIGHT.map((category) => (
                  <SkillCategory key={category.title} {...category} />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="advisors" className="advisors section light-background">
          <div className="container section-title" data-aos="fade-up">
            <h2>Advisors</h2>
            <p>Faculty mentors who have guided my doctoral and master&apos;s research.</p>
          </div>

          <div className="container">
            {ADVISOR_DEGREES.map((degree) => (
              <div key={degree.title} className="advisor-degree-block" data-aos="fade-up">
                <h3 className="advisor-degree-title">{degree.title}</h3>
                <div className="row gy-4 justify-content-center">
                  {degree.advisors.map((advisor) => (
                    <div
                      key={advisor.name}
                      className={`col-lg-${degree.advisors.length > 1 ? '6' : '8'} col-md-${degree.advisors.length > 1 ? '6' : '10'}`}
                      data-aos="fade-up"
                      data-aos-delay={advisor.delay}
                    >
                      <div className="advisor-card">
                        <img src={asset(advisor.img)} className="advisor-photo" alt={advisor.name} />
                        <div className="advisor-info">
                          <span className="advisor-role">{advisor.role}</span>
                          <h4>{advisor.name}</h4>
                          <div className="advisor-links">
                            {advisor.email && (
                              <a href={`mailto:${advisor.email}`}>{advisor.email}</a>
                            )}
                            {advisor.scholar && (
                              <a href={advisor.scholar} target="_blank" rel="noopener noreferrer">
                                Google Scholar
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="advisor-focus" data-aos="fade-up" data-aos-delay="250">
                  <h4>{degree.focusTitle}</h4>
                  <p>{degree.focus}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="personal-interests" className="portfolio section">
          <div className="container section-title" data-aos="fade-up">
            <h2>Personal Interests</h2>
          </div>

          <div className="container">
            <PoetryBookSection
              expanded={poetryBookExpanded}
              onToggle={() => setPoetryBookExpanded((prev) => !prev)}
            />

            <h3 className="poetry-book-title personal-interests-habits-title" data-aos="fade-up">
              Habits &amp; Pastimes That Shape My Days
            </h3>

            <div className="personal-interests-habits-layout">
              <div className="personal-interests-album-cta" data-aos="fade-up" data-aos-delay="50">
                <a
                  href={ALBUM_PAGE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="photo-album-btn"
                  aria-label="Open photo album in a new tab"
                >
                  <span className="photo-album-btn-icon" aria-hidden="true">
                    <i className="bi bi-images" />
                  </span>
                  <div className="photo-album-btn-preview" aria-hidden="true">
                    {PHOTO_ALBUM_IMAGES.slice(0, 4).map((photo) => (
                      <img key={photo.caption} src={photo.src} alt="" />
                    ))}
                  </div>
                  <h4>Photo Album</h4>
                  <p>Real photos from travel, friendships, and everyday life.</p>
                  <span className="photo-album-btn-action">
                    View album
                    <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                  </span>
                </a>
              </div>

              <div className="personal-interests-cards" data-aos="fade-up" data-aos-delay="100">
              <CardSwap
                width={360}
                height={460}
                cardDistance={78}
                verticalDistance={26}
                skewAmount={3}
                delay={5000}
                hoverLift={18}
              >
                {PERSONAL_INTERESTS.map((item) => (
                  <Card key={item.title} customClass="interest-card">
                    <img
                      src={asset(item.img)}
                      className="interest-card-image"
                      alt={item.title}
                    />
                    <div className="interest-card-overlay">
                      <h4>{item.title}</h4>
                      <p>{item.desc}</p>
                    </div>
                  </Card>
                ))}
              </CardSwap>
              </div>
            </div>
          </div>
        </section>

        <section id="testimonials" className="testimonials section light-background">
          <div className="container section-title" data-aos="fade-up">
            <h2>Testimonials</h2>
            <p>Reflections from mentors, professors, and colleagues who have shaped my academic and professional path.</p>
          </div>

          <div className="container" data-aos="fade-up" data-aos-delay="100">
            <div className="swiper init-swiper">
              <div className="swiper-wrapper">
                {TESTIMONIALS.map((item) => (
                  <div key={item.name} className="swiper-slide">
                    <div className="testimonial-item">
                      <p>
                        <i className="bi bi-quote quote-icon-left" />
                        <span>{item.quote}</span>
                        <i className="bi bi-quote quote-icon-right" />
                      </p>
                      <img src={asset(item.img)} className="testimonial-img" alt="" />
                      <h3>{item.name}</h3>
                      <h4>{item.role}</h4>
                    </div>
                  </div>
                ))}
              </div>
              <div className="swiper-pagination" />
            </div>
          </div>
        </section>

        <section id="contact" className="contact section">
          <div className="container section-title" data-aos="fade-up">
            <h2>Direct Message</h2>
            <p>Have something on your mind? I&apos;d love to hear from you. Send me a note anytime.</p>
          </div>

          <div className="container" data-aos="fade-up" data-aos-delay="100">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <form
                  className="php-email-form direct-message-form"
                  data-aos="fade-up"
                  data-aos-delay="200"
                  onSubmit={handleDirectMessageSubmit}
                >
                  <div className="row gy-4">
                    <div className="col-md-6">
                      <label htmlFor="name-field" className="pb-2">Your Name</label>
                      <input type="text" name="name" id="name-field" className="form-control" required />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="email-field" className="pb-2">Your Email</label>
                      <input type="email" className="form-control" name="email" id="email-field" required />
                    </div>
                    <div className="col-md-12">
                      <label htmlFor="message-field" className="pb-2">Message</label>
                      <textarea
                        className="form-control"
                        name="message"
                        rows={8}
                        id="message-field"
                        required
                        placeholder="Feel free to write whatever you'd like. Questions, ideas, or just a friendly hello. I'm genuinely happy to hear from you."
                      />
                    </div>
                    <div className="col-md-12 text-center">
                      <div className={`loading${messageStatus === 'loading' ? ' d-block' : ''}`}>Sending...</div>
                      <div className={`error-message${messageStatus === 'error' ? ' d-block' : ''}`}>
                        Something went wrong. Please try again in a moment.
                      </div>
                      <div className={`sent-message${messageStatus === 'success' ? ' d-block' : ''}`}>
                        Thank you for writing for me!
                      </div>
                      <button type="submit" disabled={messageStatus === 'loading'}>
                        Send
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer id="footer" className={`footer position-relative light-background${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
        <div className="container">
          <div className="credits text-center">
            <p>Inspired by iPortfolio page.</p>
            <p>Modified by Ehsan Faghih.</p>
          </div>
        </div>
      </footer>

      <a href="#hero" id="scroll-top" className="scroll-top d-flex align-items-center justify-content-center"><i className="bi bi-arrow-up-short" /></a>
      <div id="preloader" className="preloader--landing d-flex align-items-center justify-content-center">
        <div className="preloader-content">
          <img src={`${process.env.PUBLIC_URL}/favicon.ico`} alt="" className="preloader-icon" aria-hidden="true" />
          <p className="preloader-message">Welcome to My World</p>
        </div>
      </div>
    </>
  );
}

export default IPortfolioPage;
